import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { settingsService } from '../../../lib/services';

const DeploymentSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');

  const [buildEnv, setBuildEnv] = useState('production');
  const [customDomain, setCustomDomain] = useState('');
  const [cdnUrl, setCdnUrl] = useState('');
  const [cacheTtl, setCacheTtl] = useState('3600');
  const [compressionEnabled, setCompressionEnabled] = useState(true);
  const [minifyAssets, setMinifyAssets] = useState(true);
  const [sourceMapsEnabled, setSourceMapsEnabled] = useState(false);
  const [robotsNoindex, setRobotsNoindex] = useState(false);

  const envOptions = [
    { value: 'development', label: 'Development' },
    { value: 'staging', label: 'Staging' },
    { value: 'production', label: 'Production' },
  ];

  const cacheTtlOptions = [
    { value: '300', label: '5 minutes' },
    { value: '900', label: '15 minutes' },
    { value: '1800', label: '30 minutes' },
    { value: '3600', label: '1 hour' },
    { value: '86400', label: '24 hours' },
    { value: '604800', label: '7 days' },
  ];

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await settingsService?.getByCategory('deployment');
        if (data?.length) {
          data?.forEach(s => {
            switch (s?.settingKey) {
              case 'build_env': setBuildEnv(s?.settingValue || 'production'); break;
              case 'custom_domain': setCustomDomain(s?.settingValue || ''); break;
              case 'cdn_url': setCdnUrl(s?.settingValue || ''); break;
              case 'cache_ttl': setCacheTtl(s?.settingValue || '3600'); break;
              case 'compression_enabled': setCompressionEnabled(s?.settingValue !== 'false'); break;
              case 'minify_assets': setMinifyAssets(s?.settingValue !== 'false'); break;
              case 'source_maps_enabled': setSourceMapsEnabled(s?.settingValue === 'true'); break;
              case 'robots_noindex': setRobotsNoindex(s?.settingValue === 'true'); break;
            }
          });
        }
      } catch (e) {
        console.error('Failed to load deployment settings:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError('');
    try {
      const settings = [
        { settingKey: 'build_env', settingValue: buildEnv, settingType: 'string', category: 'deployment' },
        { settingKey: 'custom_domain', settingValue: customDomain, settingType: 'string', category: 'deployment' },
        { settingKey: 'cdn_url', settingValue: cdnUrl, settingType: 'string', category: 'deployment' },
        { settingKey: 'cache_ttl', settingValue: cacheTtl, settingType: 'number', category: 'deployment' },
        { settingKey: 'compression_enabled', settingValue: String(compressionEnabled), settingType: 'boolean', category: 'deployment' },
        { settingKey: 'minify_assets', settingValue: String(minifyAssets), settingType: 'boolean', category: 'deployment' },
        { settingKey: 'source_maps_enabled', settingValue: String(sourceMapsEnabled), settingType: 'boolean', category: 'deployment' },
        { settingKey: 'robots_noindex', settingValue: String(robotsNoindex), settingType: 'boolean', category: 'deployment' },
      ];
      const { error } = await settingsService?.upsert(settings);
      if (error) throw error;
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err?.message || 'Failed to save deployment settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={24} className="text-primary animate-spin mr-2" />
        <span className="text-muted-foreground">Loading deployment settings...</span>
      </div>
    );
  }

  const envBadge = {
    development: { color: 'bg-warning/10 text-warning', label: 'Development' },
    staging: { color: 'bg-accent/10 text-accent', label: 'Staging' },
    production: { color: 'bg-success/10 text-success', label: 'Production' },
  };

  return (
    <div className="space-y-6">
      {saveSuccess && (
        <div className="flex items-center gap-2 p-3 bg-success/10 border border-success/20 rounded-lg text-success text-sm">
          <Icon name="CheckCircle" size={16} />
          Deployment settings saved successfully!
        </div>
      )}
      {saveError && (
        <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/20 rounded-lg text-error text-sm">
          <Icon name="AlertCircle" size={16} />
          {saveError}
        </div>
      )}
      {/* Environment Status */}
      <div className="bg-card rounded-lg border border-border p-5">
        <div className="flex items-start justify-between gap-3 mb-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name="Server" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">Environment Configuration</h3>
              <p className="text-sm text-muted-foreground">Set the current deployment environment</p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${envBadge?.[buildEnv]?.color}`}>
            {envBadge?.[buildEnv]?.label}
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select label="Build Environment" options={envOptions} value={buildEnv} onChange={setBuildEnv} />
          <Input
            label="Custom Domain"
            type="url"
            value={customDomain}
            onChange={e => setCustomDomain(e?.target?.value)}
            placeholder="https://yourdomain.com"
            description="Your production domain URL"
          />
        </div>
      </div>
      {/* Build Optimisation */}
      <div className="bg-card rounded-lg border border-border p-5">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Zap" size={20} className="text-success" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground mb-1">Build Optimisation</h3>
            <p className="text-sm text-muted-foreground">Configure production build performance settings</p>
          </div>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="CDN Base URL"
              type="url"
              value={cdnUrl}
              onChange={e => setCdnUrl(e?.target?.value)}
              placeholder="https://cdn.yourdomain.com"
              description="Optional CDN for static assets"
            />
            <Select label="Cache TTL" options={cacheTtlOptions} value={cacheTtl} onChange={setCacheTtl} />
          </div>
          <div className="space-y-3 pt-1">
            <Checkbox
              label="Enable Compression (gzip/brotli)"
              description="Compress responses to reduce bandwidth usage"
              checked={compressionEnabled}
              onChange={e => setCompressionEnabled(e?.target?.checked)}
            />
            <Checkbox
              label="Minify Assets (JS/CSS)"
              description="Remove whitespace and comments from production assets"
              checked={minifyAssets}
              onChange={e => setMinifyAssets(e?.target?.checked)}
            />
            <Checkbox
              label="Generate Source Maps"
              description="Enable source maps for debugging (not recommended in production)"
              checked={sourceMapsEnabled}
              onChange={e => setSourceMapsEnabled(e?.target?.checked)}
            />
          </div>
        </div>
      </div>
      {/* SEO & Indexing */}
      <div className="bg-card rounded-lg border border-border p-5">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Search" size={20} className="text-warning" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground mb-1">SEO & Indexing</h3>
            <p className="text-sm text-muted-foreground">Control how search engines interact with your site</p>
          </div>
        </div>
        <Checkbox
          label="Block Search Engine Indexing (noindex)"
          description="Add a global noindex meta tag — useful for staging environments. Disable before going live."
          checked={robotsNoindex}
          onChange={e => setRobotsNoindex(e?.target?.checked)}
        />
        {robotsNoindex && (
          <div className="mt-3 flex items-center gap-2 p-3 bg-warning/10 border border-warning/20 rounded-lg text-warning text-xs">
            <Icon name="AlertTriangle" size={14} />
            Search engines will not index your site while this is enabled.
          </div>
        )}
      </div>
      {/* Build Checklist */}
      <div className="bg-card rounded-lg border border-border p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
            <Icon name="ClipboardCheck" size={20} className="text-accent" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground mb-1">Production Readiness Checklist</h3>
            <p className="text-sm text-muted-foreground">Verify your site is ready for production</p>
          </div>
        </div>
        <div className="space-y-2">
          {[
            { label: 'Environment set to Production', done: buildEnv === 'production' },
            { label: 'Custom domain configured', done: customDomain?.length > 0 },
            { label: 'Asset minification enabled', done: minifyAssets },
            { label: 'Compression enabled', done: compressionEnabled },
            { label: 'Source maps disabled', done: !sourceMapsEnabled },
            { label: 'Search indexing enabled (noindex off)', done: !robotsNoindex },
          ]?.map((item, i) => (
            <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-muted/30">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${item?.done ? 'bg-success/20' : 'bg-muted'}`}>
                <Icon name={item?.done ? 'Check' : 'Minus'} size={12} className={item?.done ? 'text-success' : 'text-muted-foreground'} />
              </div>
              <span className={`text-sm ${item?.done ? 'text-foreground' : 'text-muted-foreground'}`}>{item?.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => window.location?.reload()}>Cancel</Button>
        <Button variant="default" loading={saving} iconName="Save" iconPosition="left" onClick={handleSave}>
          Save Deployment Settings
        </Button>
      </div>
    </div>
  );
};

export default DeploymentSettings;
